import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import NorthEastRoundedIcon from '@mui/icons-material/NorthEastRounded'
import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import Link from 'next/link'
import { ReactNode, useState } from 'react'
import FAQ from 'src/components/FAQ'
import { getStartingPrice } from 'src/constants/cityPriceFloors'
import type { CitySeoOverride } from 'src/constants/citySeoContent'
import {
  HOOD_MAP,
  METRO_PARENT_MAP,
  PILLAR_NS_MAP,
  PILLAR_PAGE_MAP,
  PillarPage,
  SEARCH_PAGE_CITY_MAP,
  SearchHood,
  SearchPageCity
} from 'src/constants/searchPages'
import usePillarNames from 'src/hooks/usePillarNames'
import { RedirectUrls } from 'src/types/types'
import { resolveOnDemandCity } from 'src/utils/onDemand'

type SizeCard = {
  i18nKey: string
  image: string
  sizeFilter: string
}

const STORAGE_SIZES: SizeCard[] = [
  {
    i18nKey: 'XSMALL',
    image: '/images/sizes/small/small-closet-v2.webp',
    sizeFilter: 'XSMALL'
  },
  {
    i18nKey: 'SMALL',
    image: '/images/sizes/small/small-studio-v2.webp',
    sizeFilter: 'SMALL'
  },
  {
    i18nKey: 'MEDIUM',
    image: '/images/sizes/medium/medium-2-bedroom-v2.webp',
    sizeFilter: 'MEDIUM'
  },
  {
    i18nKey: 'LARGE',
    image: '/images/sizes/large/large-3-bedroom-v2.webp',
    sizeFilter: 'LARGE'
  }
]

const PARKING_SIZES: SizeCard[] = [
  {
    i18nKey: 'MOTO',
    image: '/images/sizes/vehicle/moto-v2.webp',
    sizeFilter: 'PARKING'
  },
  {
    i18nKey: 'AUTO',
    image: '/images/sizes/vehicle/auto-compact-v2.webp',
    sizeFilter: 'PARKING'
  },
  {
    i18nKey: 'CAMIONETA',
    image: '/images/sizes/vehicle/suv-v2.webp',
    sizeFilter: 'PARKING'
  },
  {
    i18nKey: 'FLOTILLA',
    image: '/images/sizes/flotilla/van-v2.webp',
    sizeFilter: 'PARKING'
  }
]

/** Generic pillar FAQs shown on a search page. The page's FAQPage JSON-LD
 * fallback uses the same cap so markup never lists hidden questions. */
export const FAQ_VISIBLE_COUNT = 6

type IProps = {
  pillar: PillarPage
  city: SearchPageCity | null
  hood: SearchHood | null
  citySeo?: CitySeoOverride | null
}

type CityContentData = {
  TITLE: string
  PARAGRAPHS: string[]
}

function SeoFooter({ pillar, city, hood, citySeo }: IProps) {
  const { t } = useTranslation(PILLAR_NS_MAP[pillar])
  const { t: tStats } = useTranslation('platform_stats')
  const { t: tCommon } = useTranslation('common')

  const interp = {
    cities: tStats('CITIES')
  }

  const { showSizeSection, startingPrice } = PILLAR_PAGE_MAP[pillar]
  // Locale-aware — `PILLAR_PAGE_MAP.searchName` is Spanish-only and gets
  // interpolated into otherwise-translated sentences all over this footer.
  const { searchName } = usePillarNames(pillar)
  const { nearbyCities, cityName, hoods } = city
    ? SEARCH_PAGE_CITY_MAP[city]
    : {}
  const { hoodName } = hood ? (HOOD_MAP[hood] ?? {}) : {}
  const locationName = hoodName || cityName || 'cerca de ti'

  // On-demand resolution for the current page. Try hood first (most-specific),
  // fall back to city — so a CDMX hood like `polanco` (not explicitly listed
  // in `metroAreaCities`) still resolves to the parent CDMX on-demand market
  // via the city slug. The internal-link section below renders only when the
  // resolved market is partner-active AND the pillar is SelfStorage (the only
  // pillar with on-demand surface today).
  const resolvedOnDemand =
    (hood ? resolveOnDemandCity(hood) : null) ??
    (city ? resolveOnDemandCity(city) : null)
  const onDemandActive =
    pillar === PillarPage.SelfStorage &&
    resolvedOnDemand !== null &&
    resolvedOnDemand.partnerActive
  const onDemandUrl = onDemandActive
    ? `/${PillarPage.SelfStorage}/almacenamiento-a-domicilio/${resolvedOnDemand.config.canonicalSlug}`
    : null

  // Nearby threading: a nearby city/hood gets an on-demand link augment ONLY
  // when its resolved canonical matches the current page's resolved canonical
  // AND that market is partner-active. The `isHood` flag enables a parent-city
  // fallback for hood slugs that aren't explicitly listed in `metroAreaCities`
  // (e.g. CDMX hoods like Polanco/Condesa). For nearby *cities*, fallback is
  // disabled so that a city covered by a *different* market is never threaded.
  const buildNearbyOnDemand = (
    slug: string,
    isHood: boolean
  ): { href: string } | null => {
    if (!onDemandActive || !resolvedOnDemand) return null
    const direct = resolveOnDemandCity(slug)
    const nearbyResolved =
      direct ?? (isHood && city ? resolveOnDemandCity(city) : null)
    if (!nearbyResolved?.partnerActive) return null
    if (nearbyResolved.city !== resolvedOnDemand.city) return null
    return {
      href: `/${PillarPage.SelfStorage}/almacenamiento-a-domicilio/${nearbyResolved.config.canonicalSlug}`
    }
  }
  // Sort by slug (URL) with a code-point comparison, NOT localeCompare.
  // localeCompare consults ICU collation, which can differ between Node and the
  // browser (the same family of bug as toLocaleString — see the hydration rules)
  // and would itself become a hydration hazard. Slugs are ASCII kebab-case, so a
  // plain `<`/`>` compare is both deterministic across runtimes AND correctly
  // alphabetical. The point of sorting at all: the rendered order is now
  // independent of MexicoCityHoods / SearchPageCity *enum declaration order*, so
  // reordering an enum no longer shifts which entries land in the slice(0, 16)
  // window below. Previously a reorder made stale CDN-cached SSR HTML (built by
  // the old bundle) disagree with a newer client bundle and fire React #418 on
  // this footer (e.g. SSR "Anzures" vs CSR "Iztapalapa" on CDMX listing pages).
  const nearby = (
    hoods
      ? hoods.map((h) => ({
          name: HOOD_MAP[h].hoodName,
          url: `/${pillar}/${city}/${h}`,
          isActive: hood === h,
          onDemand: buildNearbyOnDemand(h, true)
        }))
      : nearbyCities
        ? nearbyCities.map((c) => ({
            name: SEARCH_PAGE_CITY_MAP[c].cityName,
            url: `/${pillar}/${c}`,
            isActive: city === c,
            onDemand: buildNearbyOnDemand(c, false)
          }))
        : []
  ).sort((a, b) => (a.url < b.url ? -1 : a.url > b.url ? 1 : 0))

  const sizes = pillar === PillarPage.Parking ? PARKING_SIZES : STORAGE_SIZES

  // FAQ data (city-specific override or pillar default), shaped for the
  // canonical FAQ component
  const faqItems = citySeo?.FAQS
    ? citySeo.FAQS.map((f) => ({ title: f.question, description: f.answer }))
    : (
        t('FAQS.ITEMS', { returnObjects: true, ...interp }) as {
          title: string
          description: string
        }[]
      ).slice(0, FAQ_VISIBLE_COUNT)

  const nearbyLabel = hood
    ? tCommon('SEO_FOOTER.NEARBY_LABEL_HOODS')
    : tCommon('SEO_FOOTER.NEARBY_LABEL_CITIES')
  const nearbyUnit = hood
    ? tCommon('SEO_FOOTER.NEARBY_UNIT_HOOD')
    : tCommon('SEO_FOOTER.NEARBY_UNIT_CITY')

  return (
    <div className="border-t border-ink-100/15 bg-grey-250 md:bg-cream-300">
      <div className="mx-auto max-w-7xl px-5 pt-14 pb-10 md:px-8 md:pt-20 md:pb-16 [&>section:first-child]:border-t-0 [&>section:first-child]:pt-0 md:[&>section:first-child]:pt-0">
        {/* Neighborhood / city editorial context */}
        {(() => {
          if (!city) return null
          const allCityContent = t('CITY_CONTENT', {
            returnObjects: true,
            defaultValue: {}
          }) as Record<string, CityContentData>
          const metroParent = METRO_PARENT_MAP[city]
          const cityData =
            allCityContent?.[city] ||
            (metroParent && allCityContent?.[metroParent]) ||
            null
          if (!cityData?.TITLE || !cityData?.PARAGRAPHS?.length) return null
          return (
            <section className="mb-14 border-t border-ink-100/15 pt-12 md:mb-20 md:pt-16">
              <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
                Contexto local
              </p>
              <h2 className="mt-3 max-w-2xl font-display text-[26px] leading-[1.15] font-medium text-ink-700 md:text-[40px]">
                {cityData.TITLE}
              </h2>
              <div className="mt-6 grid grid-cols-1 gap-x-10 gap-y-4 text-[15px] leading-[1.75] text-ink-200 md:mt-8 md:grid-cols-2 md:gap-y-5 md:text-base">
                {cityData.PARAGRAPHS.map((paragraph, i) => (
                  <p key={i}>{paragraph}</p>
                ))}
              </div>
            </section>
          )
        })()}

        {/* Nearby — Airbnb-style colonia/ciudad cards */}
        {nearby.length > 0 && (
          <section className="mb-14 border-t border-ink-100/15 pt-12 md:mb-20 md:pt-16">
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between md:gap-8">
              <div>
                <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
                  {tCommon('SEO_FOOTER.NEARBY_EYEBROW', { locationName })}
                </p>
                <h2 className="mt-3 max-w-2xl font-display text-[26px] leading-[1.15] font-medium text-ink-700 md:text-[40px]">
                  {tCommon('SEO_FOOTER.NEARBY_TITLE_LEAD', {
                    searchName: searchName.toLowerCase()
                  })}
                  <span className="italic">
                    {tCommon('SEO_FOOTER.NEARBY_TITLE_ACCENT', {
                      label: nearbyLabel
                    })}
                  </span>
                </h2>
              </div>
              <p className="max-w-xs text-[14px] leading-relaxed text-ink-200">
                {tCommon('SEO_FOOTER.NEARBY_SUBTITLE', { unit: nearbyUnit })}
              </p>
            </div>
            <ul className="mt-8 grid grid-cols-2 gap-3 md:mt-10 md:grid-cols-3 md:gap-4 lg:grid-cols-4">
              {nearby.slice(0, 16).map((item) => (
                <li key={item.name} className="flex flex-col">
                  <Link
                    href={item.url}
                    aria-current={item.isActive ? 'page' : undefined}
                    className={`group relative flex h-full min-h-[112px] flex-col justify-between border p-4 transition-all duration-200 md:min-h-[128px] md:p-5 ${
                      item.isActive
                        ? 'border-blue-500 bg-blue-500 text-white-500'
                        : 'border-ink-100/20 bg-white-500 text-ink-700 hover:border-blue-500'
                    }`}
                  >
                    <span
                      className={`font-display text-[18px] leading-[1.15] font-medium md:text-[22px] ${
                        item.isActive ? 'text-white-500' : 'text-ink-700'
                      }`}
                    >
                      {item.name}
                    </span>
                    <span
                      className={`mt-2 flex items-center justify-between gap-2 text-[11px] tracking-[0.06em] uppercase ${
                        item.isActive
                          ? 'text-white-500/80'
                          : 'text-ink-200 group-hover:text-blue-500'
                      }`}
                    >
                      <span>
                        {item.isActive
                          ? tCommon('SEO_FOOTER.CURRENT_LOCATION')
                          : tCommon('SEO_FOOTER.VIEW_TERM', {
                              term: searchName.split(' ')[0].toLowerCase()
                            })}
                      </span>
                      <NorthEastRoundedIcon
                        className="text-[14px] transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                        fontSize="inherit"
                      />
                    </span>
                  </Link>
                  {item.onDemand && (
                    <Link
                      href={item.onDemand.href}
                      className="mt-2 inline-flex items-center gap-1 text-[11px] tracking-[0.04em] text-ink-200 underline decoration-blue-500/40 underline-offset-[3px] transition-colors hover:text-blue-800 hover:decoration-blue-500"
                    >
                      <span>{`${item.name} · ${t('SEO_FOOTER_ON_DEMAND.NEARBY_CTA')}`}</span>
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Comparison — editorial rows */}
        <section className="mb-14 border-t border-ink-100/15 pt-12 md:mb-20 md:pt-16">
          <div className="md:grid md:grid-cols-12 md:gap-12">
            <div className="md:col-span-4">
              <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
                {tCommon('SEO_FOOTER.COMPARISON_EYEBROW')}
              </p>
              <h2 className="mt-3 font-display text-[26px] leading-[1.15] font-medium text-ink-700 md:text-[40px]">
                {t('COMPARISON.TITLE')}
              </h2>
              <p className="mt-4 max-w-sm text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
                {t('COMPARISON.SUBTITLE')}
              </p>
            </div>
            <div className="mt-8 md:col-span-8 md:mt-0">
              <div className="flex items-baseline justify-end gap-4 pb-3 md:gap-10">
                <span className="font-display text-[13px] font-medium text-blue-500 md:text-xs md:tracking-[0.14em] md:uppercase">
                  SpotMe
                </span>
                <span className="w-16 text-center text-[12px] font-medium text-ink-200 md:w-24 md:text-xs md:font-normal md:tracking-[0.14em] md:uppercase">
                  <span className="md:hidden">
                    {t('COMPARISON.COMPETENCE_SHORT')}
                  </span>
                  <span className="hidden md:inline">
                    {t('COMPARISON.COMPETENCE')}
                  </span>
                </span>
              </div>
              <ul className="border-t border-ink-100/15">
                {t('COMPARISON.ITEMS', { returnObjects: true, ...interp }).map(
                  (item, index) => (
                    <li
                      key={index}
                      className="flex items-center justify-between gap-3 border-b border-ink-100/15 py-4 text-[14px] text-ink-700 md:gap-6 md:py-5 md:text-[15px]"
                    >
                      <span className="min-w-0 flex-1 pr-1 md:pr-2">
                        {item.title}
                      </span>
                      <div className="flex shrink-0 items-center gap-3 md:gap-8">
                        <span
                          className="flex w-16 items-center justify-center md:w-24 md:justify-end"
                          aria-label={
                            item.spotMeInsurance
                              ? tCommon('SEO_FOOTER.A11Y_SPOTME_OFFERS', {
                                  item: item.title
                                })
                              : tCommon('SEO_FOOTER.A11Y_SPOTME_NOT_OFFERS', {
                                  item: item.title
                                })
                          }
                        >
                          {item.spotMeInsurance ? (
                            <span className="flex size-6 items-center justify-center rounded-full bg-blue-500 text-white-500">
                              <CheckGlyph />
                            </span>
                          ) : (
                            <span className="flex size-6 items-center justify-center rounded-full border border-ink-100/40 text-ink-100">
                              <DashGlyph />
                            </span>
                          )}
                        </span>
                        <span
                          className="flex w-16 items-center justify-center md:w-24 md:justify-end"
                          aria-label={
                            item.otherInsurance
                              ? tCommon('SEO_FOOTER.A11Y_OTHERS_OFFER', {
                                  item: item.title
                                })
                              : tCommon('SEO_FOOTER.A11Y_OTHERS_NOT_OFFER', {
                                  item: item.title
                                })
                          }
                        >
                          {item.otherInsurance ? (
                            <span className="flex size-6 items-center justify-center rounded-full border border-ink-200 text-ink-200">
                              <CheckGlyph />
                            </span>
                          ) : (
                            <span className="flex size-6 items-center justify-center rounded-full border border-ink-100/40 text-ink-100">
                              <DashGlyph />
                            </span>
                          )}
                        </span>
                      </div>
                    </li>
                  )
                )}
              </ul>
              <p className="mt-4 text-[12px] text-ink-200">
                {`${t('COMPARISON.INFO.TEXT_1')} `}
                <span className="text-ink-700 underline decoration-blue-500/40 underline-offset-[3px]">
                  {t('COMPARISON.INFO.LINK')}
                </span>
              </p>
            </div>
          </div>
        </section>

        {/* Size guide */}
        {showSizeSection && (
          <section className="mb-14 border-t border-ink-100/15 pt-12 md:mb-20 md:pt-16">
            <div className="md:flex md:items-end md:justify-between md:gap-8">
              <div>
                <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
                  {tCommon('PILLAR_SECTIONS.EYEBROW_SIZES')}
                </p>
                <h2 className="mt-3 max-w-2xl font-display text-[26px] leading-[1.15] font-medium text-ink-700 md:text-[40px]">
                  {t('STEPS.TITLE')}
                </h2>
                <p className="mt-4 max-w-lg text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
                  {t('STEPS.SUBTITLE_1')}
                </p>
              </div>
              <Link
                href={RedirectUrls.SizeGuide}
                className="mt-5 hidden shrink-0 text-[13px] font-medium tracking-[0.08em] text-ink-700 uppercase underline decoration-blue-500/40 underline-offset-[6px] transition-colors hover:text-blue-800 hover:decoration-blue-500 md:mt-0 md:inline-block"
              >
                {t('STEPS.SIZE_GUIDE_LINK')}
              </Link>
            </div>
            <ul className="scrollbar-hide mt-8 flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2 md:mt-10 md:grid md:snap-none md:grid-cols-4 md:gap-5 md:overflow-visible md:pb-0">
              {sizes.map((size, i) => (
                <li
                  key={size.i18nKey}
                  className="w-[220px] shrink-0 snap-start md:w-auto"
                >
                  <Link
                    href={`${RedirectUrls.Search}?sizes=${size.sizeFilter}`}
                    className="group flex h-full flex-col overflow-hidden bg-white-500 transition-colors hover:bg-cream-200"
                  >
                    <div className="relative aspect-[5/4] overflow-hidden bg-white-500">
                      <span
                        aria-hidden="true"
                        className="absolute top-3 left-3 z-10 rounded-full bg-white-500/95 px-2 py-0.5 font-display text-[14px] text-blue-500 italic shadow-sm md:text-[16px]"
                      >
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <Image
                        src={size.image}
                        alt={t(`STEPS.SIZES.${size.i18nKey}.TITLE` as never)}
                        fill
                        sizes="(max-width: 900px) 220px, 25vw"
                        style={{ objectFit: 'contain' }}
                        quality={85}
                        className="p-4 transition-transform duration-500 group-hover:scale-[1.03]"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex flex-1 flex-col border-t border-ink-100/15 px-4 py-4">
                      <h3 className="font-display text-[17px] leading-tight font-medium text-ink-700 md:text-[19px]">
                        {t(`STEPS.SIZES.${size.i18nKey}.TITLE` as never)}
                      </h3>
                      <p className="mt-1 text-[13px] leading-relaxed text-ink-200">
                        {t(`STEPS.SIZES.${size.i18nKey}.DESC` as never)}
                      </p>
                      <span className="mt-3 inline-flex items-center gap-1 text-[11px] tracking-[0.08em] text-blue-500 uppercase transition-colors group-hover:text-ink-700">
                        {t('STEPS.BUTTON_PHOLDER')}
                        <NorthEastRoundedIcon
                          className="text-[14px] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                          fontSize="inherit"
                        />
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-6 md:hidden">
              <Link
                href={RedirectUrls.SizeGuide}
                className="text-[13px] font-medium tracking-[0.08em] text-ink-700 uppercase underline decoration-blue-500/40 underline-offset-[6px]"
              >
                {t('STEPS.SIZE_GUIDE_LINK')}
              </Link>
            </div>
          </section>
        )}

        {!showSizeSection && (
          <section className="mb-14 border-t border-ink-100/15 pt-12 md:mb-20 md:pt-16">
            <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
              Tipos de espacio
            </p>
            <h2 className="mt-3 font-display text-[26px] leading-[1.15] font-medium text-ink-700 md:text-[40px]">
              {t('SPACE_TYPES.TITLE')}
            </h2>
            <ul className="mt-6 grid gap-2 md:mt-8 md:grid-cols-2 md:gap-3">
              {t('SPACE_TYPES.ITEMS', { returnObjects: true }).map(
                (type, index) => (
                  <li
                    key={index}
                    className="flex items-center gap-3 border-b border-ink-100/15 py-3 text-[14px] text-ink-700 md:text-[15px]"
                  >
                    <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-blue-500/10 text-blue-500">
                      <CheckGlyph />
                    </span>
                    <span>{type}</span>
                  </li>
                )
              )}
            </ul>
          </section>
        )}

        {/* Features — editorial numbered grid */}
        <section className="mb-14 border-t border-ink-100/15 pt-12 md:mb-20 md:pt-16">
          <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
            {tCommon('SEO_FOOTER.WHY_SPOTME_EYEBROW')}
          </p>
          <h2 className="mt-3 max-w-2xl font-display text-[26px] leading-[1.15] font-medium text-ink-700 md:text-[40px]">
            {t('FEATURES.TITLE')}
          </h2>
          <ul className="mt-8 grid gap-6 md:mt-12 md:grid-cols-2 md:gap-x-10 md:gap-y-8 lg:grid-cols-3">
            {t('FEATURES.ITEMS', { returnObjects: true, ...interp }).map(
              (item, index) => (
                <li
                  key={index}
                  className="flex flex-col border-t border-ink-100/20 pt-5"
                >
                  <span className="font-display text-[14px] text-blue-500 italic md:text-[15px]">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <h3 className="mt-2 font-display text-[18px] leading-tight font-medium text-ink-700 md:text-[20px]">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
                    {item.subtitle}
                  </p>
                </li>
              )
            )}
          </ul>
        </section>

        {/* City-specific editorial collapsibles */}
        {citySeo?.EDITORIAL && citySeo.EDITORIAL.length > 0 && (
          <EditorialSections sections={citySeo.EDITORIAL} />
        )}
      </div>

      {/* FAQ — delegates to canonical editorial FAQ component (its own
          bg-cream-300 band matches the SeoFooter wrapper). */}
      <FAQ
        title={t('FAQS.TITLE')}
        faqs={faqItems}
        titleAs="h2"
        whatsappMsg={t('FAQS.WHATSAPP_MSG')}
        surface="search"
      />

      {/* Cross-pillar + about — editorial tiles */}
      <div className="mx-auto max-w-7xl px-5 py-12 md:px-8 md:py-16">
        {city && (
          <section className="border-t border-ink-100/15 pt-12 md:pt-16">
            <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
              {tCommon('SEO_FOOTER.OTHER_SPACES_EYEBROW', { cityName })}
            </p>
            <h2 className="mt-3 max-w-2xl font-display text-[22px] leading-[1.15] font-medium text-ink-700 md:text-[32px]">
              {tCommon('SEO_FOOTER.BESIDES', {
                searchName: searchName.toLowerCase()
              })}
            </h2>
            <ul className="mt-6 grid gap-2 md:mt-8 md:grid-cols-3 md:gap-3">
              {Object.entries(PILLAR_PAGE_MAP)
                .filter(([key]) => key !== pillar)
                .map(([key, config]) => {
                  // City floor; null = no listings of that kind here → no price line.
                  const price = getStartingPrice(key as PillarPage, city)
                  return (
                    <li key={key}>
                      <Link
                        href={`/${key}/${city}`}
                        className="group flex items-center justify-between gap-4 border border-ink-100/20 bg-white-500 px-5 py-4 transition-colors hover:border-blue-500"
                      >
                        <div className="min-w-0">
                          <h3 className="font-display text-[17px] leading-tight font-medium text-ink-700 group-hover:text-blue-800 md:text-[19px]">
                            {config.label}
                          </h3>
                          {price !== null && (
                            <p className="mt-1 text-[12px] tracking-[0.06em] text-ink-200 uppercase">
                              {`Desde ${price}/mes`}
                            </p>
                          )}
                        </div>
                        <NorthEastRoundedIcon
                          className="shrink-0 text-ink-200 transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-blue-500"
                          fontSize="small"
                        />
                      </Link>
                    </li>
                  )
                })}
            </ul>
          </section>
        )}

        {/* On-demand internal-link — surfaces only on the SelfStorage pillar
            when the current city/hood resolves to a covered, partner-active
            on-demand market. Visually moderate: hairline-bordered editorial
            row. Below-fold internal-link compounding, not a hero card. */}
        {onDemandActive && onDemandUrl && resolvedOnDemand && (
          <section className="mt-14 border-t border-ink-100/15 pt-12 md:mt-20 md:pt-16">
            <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
              Almacenamiento a Domicilio
            </p>
            <h2 className="mt-3 max-w-2xl font-display text-[22px] leading-[1.15] font-medium text-ink-700 md:text-[32px]">
              {t('SEO_FOOTER_ON_DEMAND.TITLE', {
                city: resolvedOnDemand.config.displayName
              })}
            </h2>
            <p className="mt-4 max-w-2xl text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
              {t('SEO_FOOTER_ON_DEMAND.BODY')}
            </p>
            <Link
              href={onDemandUrl}
              className="group mt-6 inline-flex items-center gap-2 text-[13px] font-medium tracking-[0.08em] text-ink-700 uppercase underline decoration-blue-500/40 underline-offset-[6px] transition-colors hover:text-blue-800 hover:decoration-blue-500"
            >
              <span>{t('SEO_FOOTER_ON_DEMAND.CTA')}</span>
              <NorthEastRoundedIcon
                className="text-[14px] transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                fontSize="inherit"
              />
            </Link>
          </section>
        )}

        {/* Soluciones Logísticas (3PL) cross-link — B2B pillars only
            (bodegas comerciales / naves industriales). The exact ICP for 3PL
            is already reading about warehouse/industrial space here. Persistent
            editorial row (not the transient search card), nationwide, no
            coverage gate. Matching-only: SpotMe CONECTA con operadores — never
            promises operator prices/coverage. Copy lives in each B2B pillar's
            own namespace so no extra serverSideTranslations wiring is needed. */}
        {(pillar === PillarPage.Warehouse ||
          pillar === PillarPage.Industrial) && (
          <section className="mt-14 border-t border-ink-100/15 pt-12 md:mt-20 md:pt-16">
            <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
              {tCommon('SEO_FOOTER.LOGISTICS_EYEBROW')}
            </p>
            <h2 className="mt-3 max-w-2xl font-display text-[22px] leading-[1.15] font-medium text-ink-700 md:text-[32px]">
              {t('SEO_FOOTER_LOGISTICS.TITLE')}
            </h2>
            <p className="mt-4 max-w-2xl text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
              {t('SEO_FOOTER_LOGISTICS.BODY')}
            </p>
            <Link
              href={RedirectUrls.SolucionesLogisticas}
              className="group mt-6 inline-flex items-center gap-2 text-[13px] font-medium tracking-[0.08em] text-ink-700 uppercase underline decoration-blue-500/40 underline-offset-[6px] transition-colors hover:text-blue-800 hover:decoration-blue-500"
            >
              <span>{t('SEO_FOOTER_LOGISTICS.CTA')}</span>
              <NorthEastRoundedIcon
                className="text-[14px] transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                fontSize="inherit"
              />
            </Link>
          </section>
        )}

        <section className="mt-14 border-t border-ink-100/15 pt-12 md:mt-20 md:pt-16">
          <p className="text-[15px] leading-[1.7] text-ink-200 md:text-base">
            {tCommon('SEO_FOOTER.MORE_OPTIONS_LEAD')}
            <Link
              href={`/${pillar}`}
              className="font-medium text-ink-700 underline decoration-blue-500/40 underline-offset-[4px] hover:text-blue-800 hover:decoration-blue-500"
            >
              {tCommon('SEO_FOOTER.SEARCH_NATIONWIDE', {
                searchName: searchName.toLowerCase()
              })}
            </Link>
            {tCommon('SEO_FOOTER.MORE_OPTIONS_TAIL', {
              price: startingPrice,
              cities: interp.cities
            })}
          </p>
        </section>

        <section className="mt-14 border-t border-ink-100/15 pt-12 md:mt-20 md:pt-16">
          <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
            {tCommon('SEO_FOOTER.ABOUT_EYEBROW')}
          </p>
          <div className="mt-5 grid grid-cols-1 gap-x-10 gap-y-4 text-[14px] leading-[1.75] text-ink-200 md:grid-cols-2 md:text-[15px]">
            <p>
              <strong className="font-medium text-ink-700">SpotMe</strong>
              {tCommon('SEO_FOOTER.ABOUT_P1', {
                searchName: searchName.toLowerCase(),
                scope: cityName
                  ? tCommon('SEO_FOOTER.ABOUT_SCOPE_CITY', { cityName })
                  : tCommon('SEO_FOOTER.ABOUT_SCOPE_NATIONWIDE')
              })}
            </p>
            <p>{tCommon('SEO_FOOTER.ABOUT_P2')}</p>
            <p className="md:col-span-2">
              {tCommon('SEO_FOOTER.ABOUT_P3_LEAD')}
              <Link
                href={`/${PillarPage.SelfStorage}`}
                className="text-ink-700 underline decoration-blue-500/40 underline-offset-[3px] hover:text-blue-800 hover:decoration-blue-500"
              >
                {tCommon('SEO_FOOTER.ABOUT_LINK_STORAGE')}
              </Link>
              {', '}
              <Link
                href={`/${PillarPage.Warehouse}`}
                className="text-ink-700 underline decoration-blue-500/40 underline-offset-[3px] hover:text-blue-800 hover:decoration-blue-500"
              >
                {tCommon('SEO_FOOTER.ABOUT_LINK_WAREHOUSE')}
              </Link>
              {', '}
              <Link
                href={`/${PillarPage.Parking}`}
                className="text-ink-700 underline decoration-blue-500/40 underline-offset-[3px] hover:text-blue-800 hover:decoration-blue-500"
              >
                {tCommon('SEO_FOOTER.ABOUT_LINK_PARKING')}
              </Link>
              {tCommon('SEO_FOOTER.ABOUT_AND')}
              <Link
                href={`/${PillarPage.Industrial}`}
                className="text-ink-700 underline decoration-blue-500/40 underline-offset-[3px] hover:text-blue-800 hover:decoration-blue-500"
              >
                {tCommon('SEO_FOOTER.ABOUT_LINK_INDUSTRIAL')}
              </Link>
              {'.'}
            </p>
          </div>
        </section>
      </div>
    </div>
  )
}

// Thin typographic glyphs — avoid heavy filled MUI icons inside the
// comparison table. Keeps the editorial hairline rhythm consistent.
function CheckGlyph() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 12 12"
      className="size-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="2.5 6.5 5 9 9.5 3.5" />
    </svg>
  )
}

function DashGlyph() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 12 12"
      className="size-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
    >
      <line x1="3" y1="6" x2="9" y2="6" />
    </svg>
  )
}

/** Editorial bodies use Markdown-style `[text](url)` for inline links so
 *  city content can reach zone pages and sibling pillars without extra
 *  schema. Anything else stays plain text. */
const EDITORIAL_LINK_RE = /\[([^\]]+)\]\(([^)]+)\)/g

function renderEditorialContent(content: string): ReactNode[] {
  const parts: ReactNode[] = []
  let lastIndex = 0
  let key = 0
  for (const match of content.matchAll(EDITORIAL_LINK_RE)) {
    const start = match.index ?? 0
    if (start > lastIndex) parts.push(content.slice(lastIndex, start))
    parts.push(
      <Link
        key={`l-${key++}`}
        href={match[2]}
        className="underline underline-offset-4 hover:text-blue-800"
      >
        {match[1]}
      </Link>
    )
    lastIndex = start + match[0].length
  }
  if (lastIndex < content.length) parts.push(content.slice(lastIndex))
  return parts
}

/** City-specific editorial H2 sections — rendered as hairline collapsibles
 *  so they don't push the rest of the footer content too far down. */
function EditorialSections({
  sections
}: {
  sections: { title: string; content: string }[]
}) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)
  const { t: tCommon } = useTranslation('common')

  return (
    <section className="mb-14 border-t border-ink-100/15 pt-12 md:mb-20 md:pt-16">
      <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
        {tCommon('SEO_FOOTER.EDITORIAL_GUIDE_EYEBROW')}
      </p>
      <ul className="mt-6 border-t border-ink-100/20 md:mt-8">
        {sections.map((section, index) => {
          const open = expandedIndex === index
          return (
            <li key={index} className="group border-b border-ink-100/20">
              <button
                type="button"
                onClick={() => setExpandedIndex(open ? null : index)}
                aria-expanded={open}
                className="flex w-full items-center justify-between gap-6 py-5 text-left md:py-6"
              >
                <span className="flex min-w-0 items-baseline gap-4">
                  <span
                    aria-hidden="true"
                    className="font-display text-[15px] text-blue-500 italic md:text-[17px]"
                  >
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <h2 className="font-display text-[17px] leading-tight font-medium text-ink-700 md:text-[20px]">
                    {section.title}
                  </h2>
                </span>
                <span className="flex size-9 shrink-0 items-center justify-center rounded-full border border-ink-100/30 text-ink-700 transition-colors group-hover:border-blue-500 group-hover:bg-blue-500 group-hover:text-white-500">
                  <ExpandMoreIcon
                    className={`text-[18px] transition-transform duration-200 ${
                      open ? 'rotate-180' : ''
                    }`}
                    fontSize="inherit"
                  />
                </span>
              </button>
              {open && (
                <p className="pb-6 pl-9 text-[14px] leading-[1.75] text-ink-200 md:pb-7 md:pl-11 md:text-[15px]">
                  {renderEditorialContent(section.content)}
                </p>
              )}
            </li>
          )
        })}
      </ul>
    </section>
  )
}

export default SeoFooter
